import { useEffect, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Modal } from 'antd'
import { UPDATES, type UpdateEntry } from '../lib/updates'
import { t } from '../lib/i18n'

// 登录后打开网站就弹出的「新功能」卡片：每条只有标题、一句话、一个例子和「去试试」，要短。
// 没看过的条目合成一组，可以翻页，一次只弹一回；看完或关掉就记下，不再出现。
// 不是一个纯记录页 —— 想重看的人从顶栏账户菜单里的「新功能」进来，那里放全部条目。
//
// 看过与否按条目 id 存 localStorage（和 FeatureTour 同一思路），换设备会再看一次。
// 存储读不了（隐私窗口、被禁）时一律当作看过：宁可漏看，也不能每次进页面都弹。
//
// 例外是「你的 worklog hook 需要更新」：它不在 UPDATES 里，而是服务端从上报数据看出
// hook 还是旧版时（共享 props 的 ai_time_hook，见 AiTime::HookStatus）排在最后一条，
// 按钮去集成页更新。它是要人动手的事，不是看过就算 —— 关掉只是这台设备 3 天内不再提醒，
// 没更新就再出现；新功能都看过了，它也会单独弹出来。更新完服务端就不再给这个状态，自然消失。

const KEY = 'asaptodo:updates-seen'
export const OPEN_EVENT = 'asaptodo:open-whats-new'

function readSeen(): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [])
  } catch {
    return null
  }
}

function markSeen(ids: string[]) {
  try {
    const seen = readSeen() ?? new Set<string>()
    ids.forEach((id) => seen.add(id))
    window.localStorage.setItem(KEY, JSON.stringify([...seen]))
  } catch {
    // 存不住就只在这次页面里不再弹
  }
}

const HOOK_SNOOZE_KEY = 'asaptodo:hook-upgrade-snoozed'
const HOOK_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000
const HOOK_PAGE = '/developers/worklog-hook'

type HookStatus = 'missing' | 'stop_only' | 'ok' | null

const HOOK_ENTRIES: Record<'missing' | 'stop_only', UpdateEntry> = {
  missing: {
    id: 'hook-upgrade-missing',
    date: '',
    tag: t('需要你更新', 'Action needed'),
    title: t('你的 worklog hook 需要更新', 'Your worklog hook needs an update'),
    intro: t(
      '新版能自动记下每条工作日志和 AI 一起花了多久，并汇总到任务上。',
      'The new version records how long each Worklog took with AI and rolls it up to your tasks.',
    ),
    example: t('在集成页复制提示词贴给 Claude Code，它会自己装好', 'Copy the prompt from the integration page into Claude Code and it installs itself'),
    cta: { label: t('去更新', 'Update'), href: HOOK_PAGE },
  },
  stop_only: {
    id: 'hook-upgrade-stop_only',
    date: '',
    tag: t('需要你更新', 'Action needed'),
    title: t('worklog hook 还差一步', 'Worklog hook: one step left'),
    intro: t(
      '脚本已经是新版，但 ~/.claude/settings.json 里还没挂 UserPromptSubmit，记不到每轮的开始时间。',
      "The script is up to date, but UserPromptSubmit isn't registered in ~/.claude/settings.json yet, so turn start times aren't recorded.",
    ),
    example: t('照集成页补上 UserPromptSubmit 那一项', 'Add the UserPromptSubmit entry as shown on the integration page'),
    cta: { label: t('去更新', 'Update'), href: HOOK_PAGE },
  },
}

function hookSnoozed(id: string): boolean {
  try {
    const raw = window.localStorage.getItem(HOOK_SNOOZE_KEY)
    const saved = raw ? (JSON.parse(raw) as { id?: string; until?: number }) : null
    return saved?.id === id && typeof saved.until === 'number' && saved.until > Date.now()
  } catch {
    return false
  }
}

function snoozeHook(id: string) {
  try {
    window.localStorage.setItem(HOOK_SNOOZE_KEY, JSON.stringify({ id, until: Date.now() + HOOK_SNOOZE_MS }))
  } catch {
    // 存不住就只在这次页面里不再弹
  }
}

// 要提示的那一条，没有就是 null。就在集成页上的时候不弹 —— 人已经在更新了。
function hookEntry(status: HookStatus): UpdateEntry | null {
  if (status !== 'missing' && status !== 'stop_only') return null
  if (window.location.pathname === HOOK_PAGE) return null
  return HOOK_ENTRIES[status]
}

export function unreadCount(): number {
  const seen = readSeen()
  return seen ? UPDATES.filter((u) => !seen.has(u.id)).length : 0
}

export function openWhatsNew() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

function Card({ entry }: { entry: UpdateEntry }) {
  return (
    <div>
      <span className="rounded-full bg-brand-primarySoft px-2 py-0.5 text-xs font-semibold text-brand-primaryDeep">{entry.tag}</span>
      <h2 className="mb-1 mt-2 font-display text-lg font-bold leading-snug text-brand-ink">{entry.title}</h2>
      <p className="text-sm leading-relaxed text-brand-ink2">{entry.intro}</p>
      <p className="mt-3 rounded-lg bg-brand-glass2 px-3 py-2 text-sm text-brand-ink">
        {t('例：', 'e.g. ')}
        {entry.example}
      </p>
    </div>
  )
}

export default function WhatsNew() {
  const [entries, setEntries] = useState<UpdateEntry[]>([])
  const [index, setIndex] = useState(0)
  // 重看时展示全部条目，不动「已读」记录。
  const [replay, setReplay] = useState(false)
  const hookStatus = (usePage().props as { ai_time_hook?: HookStatus }).ai_time_hook ?? null

  useEffect(() => {
    const seen = readSeen()
    const unread = seen ? UPDATES.filter((u) => !seen.has(u.id)) : []
    const hook = hookEntry(hookStatus)
    const queue = hook && !hookSnoozed(hook.id) ? [...unread, hook] : unread
    if (queue.length > 0) setEntries(queue)
    // 只在挂载时排一次队：换页时 Header 不重挂，弹窗不该因为换页再冒出来
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const open = () => {
      const hook = hookEntry(hookStatus)
      setReplay(true)
      setIndex(0)
      setEntries(hook ? [...UPDATES, hook] : UPDATES)
    }
    window.addEventListener(OPEN_EVENT, open)
    return () => window.removeEventListener(OPEN_EVENT, open)
  }, [hookStatus])

  const close = () => {
    const hook = entries.find((e) => e.id.startsWith('hook-upgrade-'))
    if (hook) snoozeHook(hook.id)
    if (!replay) markSeen(entries.filter((e) => e !== hook).map((e) => e.id))
    setEntries([])
    setReplay(false)
    setIndex(0)
  }

  if (entries.length === 0) return null
  const entry = entries[index]
  const last = index === entries.length - 1

  const go = () => {
    const href = entry.cta?.href
    close()
    if (href) router.visit(href)
  }

  return (
    <Modal
      open
      centered
      width={400}
      onCancel={close}
      maskClosable={false}
      title={<span className="text-sm font-semibold text-brand-primaryStrong">
          {t('新功能', "What's new")} · {index + 1}/{entries.length}
        </span>}
      footer={
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1.5" aria-hidden="true">
            {entries.map((e, i) => (
              <span
                key={e.id}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-brand-primary' : 'w-1.5 bg-brand-line'}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {index > 0 && (
              <button type="button" className="rounded-full px-3 py-1.5 text-sm text-brand-ink2 hover:bg-brand-glass2" onClick={() => setIndex(index - 1)}>
                {t('上一条', 'Previous')}
              </button>
            )}
            {entry.cta && (
              <button type="button" className="rounded-full border border-brand-line bg-brand-glass2 px-4 py-1.5 text-sm font-semibold text-brand-ink hover:bg-brand-glass" onClick={go}>
                {entry.cta.label}
              </button>
            )}
            <button
              type="button"
              className="rounded-full bg-brand-primaryStrong px-4 py-1.5 text-sm font-semibold text-brand-onPrimary shadow-brand hover:brightness-90"
              onClick={last ? close : () => setIndex(index + 1)}
            >
              {last ? t('知道了', 'Got it') : t('下一条', 'Next')}
            </button>
          </div>
        </div>
      }
    >
      <Card entry={entry} />
    </Modal>
  )
}
